import {
  AliasType,
  ContactType,
  GetSubmissionQuery,
  Provider,
  QuoteStatus,
  SubmissionStatus,
  SubmissionSubStatus,
  SubmissionType,
} from '../gql/graphql';
import { getFirstPaginatedNode, getPaginatedNodes } from './utils';

import { getLogoForDomain } from '@converge/utils';
import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_SUBMISSION = graphql(`
  query getSubmission($uuid: String!) {
    submission(uuid: $uuid) {
      uuid
      referenceId
      type
      coverageLevelScoring
      propertyAndCasualtyLicenseUuid
      surplusLinesTaxLicenseUuid
      status
      subStatus
      policyFee
      notes
      renewal
      renewalType
      documentLink
      hasPlaceholderNaics
      propertyAndCasualtyLicense {
        expired
      }
      surplusLinesTaxLicense {
        expired
      }
      capacityProviders(sort: { order: "ASC", key: "order" }) {
        edges {
          node {
            uuid
            quote {
              quotaShare
            }

            aliases(filters: { source: PRIMARY, type: LEGAL_NAME }) {
              edges {
                node {
                  name
                }
              }
            }
          }
        }
      }
      tasks {
        edges {
          node {
            label
            uuid
            status
            dueDate
            notes {
              edges {
                node {
                  label
                }
              }
            }
            user {
              node {
                username
                authUsername
                accountName
              }
            }
          }
        }
      }
      persons {
        edges {
          cursor
          node {
            uuid
            firstName
            lastName
            agent
            title
            type
            source
            personType {
              uuid
              type
              title
            }
            primary
            contacts {
              edges {
                cursor
                node {
                  uuid
                  type
                  value
                  source
                  primary
                }
              }
            }
          }
        }
      }
      product {
        node {
          uuid
          name
        }
      }
      partner {
        node {
          uuid
          aliases {
            edges {
              cursor
              queryCursor
              node {
                name
                primary
                type
                uuid
              }
            }
          }
        }
      }
      program {
        node {
          name
          uuid
          partnered
        }
      }
      brokerEntity {
        cursor
        node {
          uuid
          referenceId
          type
          submissionType
          agency
          status
          parents {
            edges {
              node {
                uuid
                aliases(filters: { groupSources: true }) {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      sources
                      type
                      primary
                    }
                  }
                }
              }
            }
          }
          aliases(filters: { groupSources: true }) {
            edges {
              cursor
              node {
                uuid
                name
                sources
                type
                primary
              }
            }
          }
          addresses(filters: { source: PRIMARY }) {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                    }
                  }
                }
                type
                postalCode
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                source
                primary
              }
            }
          }
          persons {
            edges {
              cursor
              node {
                uuid
                firstName
                lastName
                agent
                title
                type
                source
                personType {
                  uuid
                  type
                  title
                }
                primary
                contacts {
                  edges {
                    cursor
                    node {
                      uuid
                      type
                      value
                      source
                      primary
                    }
                  }
                }
              }
            }
          }
        }
      }
      insuredEntity {
        cursor
        node {
          uuid
          referenceId
          type
          submissionType
          agency
          status

          aliases(filters: { groupSources: true }) {
            edges {
              cursor
              node {
                uuid
                name
                sources
                type
                primary
              }
            }
          }
          domains {
            edges {
              cursor
              node {
                uuid
                domain
                sources
                primary
              }
            }
          }
          addresses {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                timezone
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                type
                postalCode
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                source
                primary
              }
            }
          }
          industrySectors {
            edges {
              cursor
              node {
                uuid
                name
                naicsCode
                sources
                primary
              }
            }
          }
          fundamentals {
            edges {
              cursor
              node {
                uuid
                revenue
                employees
                projectedRevenue
                ratedRevenue
                ratedRevenueOverride
                foreignRevenue
                projectedForeignRevenue
                currentRatio
                yearEstablished
                quickRatio
                cashRatio
                sources
                primary
              }
            }
          }
          persons {
            edges {
              cursor
              node {
                uuid
                firstName
                lastName
                agent
                title
                type
                source
                personType {
                  uuid
                  type
                  title
                }
                primary
                contacts {
                  edges {
                    cursor
                    node {
                      uuid
                      type
                      value
                      source
                      primary
                    }
                  }
                }
              }
            }
          }
        }
      }
      createdAt
      updatedAt
      receivedAt
      dueDate
      aggregateLimit
      policyRetention
      policyStartDate
      policyEndDate
      brokerCommission
      prosAndCons
      descriptionOfOperations
      financialAnalysisSummary
      applications {
        edges {
          cursor
          node {
            uuid
            name
            version
            createdAt
            updatedAt
            submissionApplication {
              cursor
              node {
                uuid
                createdAt
                updatedAt
              }
            }
          }
        }
      }
      users {
        edges {
          cursor
          node {
            uuid
            username
            userId
            groups
            type
          }
        }
      }
      attachments(filters: null, pagination: null, sort: null) {
        edges {
          cursor
          node {
            uuid
            targetType
            targetName
            targetUuid
            dataSource
            filePath
            folder
            createdAt
            updatedAt
            url
          }
          cursor
          queryCursor
        }
      }
    }
    quotes(
      filters: { submissionUuid: $uuid, type: USER_QUOTE }
      sort: { order: "ASC", key: "order" }
    ) {
      edges {
        cursor
        node {
          uuid
          referenceId
          createdAt
          inForcePolicy
          updatedAt
          type
          name
          policies {
            edges {
              node {
                uuid
                fundamentals {
                  edges {
                    cursor
                    node {
                      uuid
                      revenue
                      employees
                      projectedRevenue
                      ratedRevenue
                      ratedRevenueOverride
                      foreignRevenue
                      projectedForeignRevenue
                      currentRatio
                      yearEstablished
                      quickRatio
                      cashRatio
                      sources
                      primary
                    }
                  }
                }
              }
            }
          }
          order
          quotedPremium
          limit
          retention
          ratedPremium
          technicalPremium
          modifiedCoveragePremium
          status
        }
      }
    }
  }
`);

export function useSubmissionQuery(uuid?: string) {
  const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(
    GET_SUBMISSION,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        uuid: uuid!,
      },
      fetchPolicy: 'no-cache',
      skip: !uuid,
    }
  );

  const info = useMemo(() => getInfo(data), [data]);

  return {
    loading,
    error,
    data,
    info,
    refetch,
    startPolling,
    stopPolling,
  };
}

const getInfo = (data?: GetSubmissionQuery) => {
  const submission = data?.submission;
  const propertyAndCasualtyLicenseExpired =
    data?.submission?.propertyAndCasualtyLicense?.expired;
  const surplusLinesTaxLicenseExpired =
    data?.submission?.surplusLinesTaxLicense?.expired;
  /* Policy */

  /* Type */
  const submissionType = submission?.type;
  const isExcessSubmission = submissionType === SubmissionType.Excess;
  const isPrimarySubmission = submissionType === SubmissionType.Primary;
  const policyTypeName = submission?.type?.toLowerCase() ?? 'submission';

  /* Metadata */
  const submissionReferenceId = submission?.referenceId;

  /* Status */
  const submissionStatus = submission?.status;
  const isDeclined = submissionStatus === SubmissionStatus.Declined;
  const isReferred = submissionStatus === SubmissionStatus.Referred;
  const submissionSubStatus = submission?.subStatus;

  /* Application (aka 'Questionnaire') */
  const application = getFirstPaginatedNode(submission?.applications);
  const applicationId = application?.uuid ?? '';
  const submissionApplication = application?.submissionApplication.node;
  const submissionApplicationUuid = submissionApplication?.uuid;

  /* User */
  const underwriter =
    submission && submission.users.edges.length > 0
      ? submission.users.edges[0].node
      : null;

  /* Entities */
  const insuredEntity = submission?.insuredEntity;
  const brokerEntity = submission?.brokerEntity;
  const insuredEntityDomain = getFirstPaginatedNode(
    insuredEntity?.node.domains
  );
  const parentBrokageEntityUuid =
    submission?.brokerEntity?.node.parents?.edges[0]?.node?.uuid;
  const insuredEntityDomainName = insuredEntityDomain?.domain;
  const insuredLogo = insuredEntityDomainName
    ? getLogoForDomain(insuredEntityDomainName)
    : undefined;
  const insuredAlias =
    insuredEntity?.node.aliases.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Primary) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Converge) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find(
      (alias) => alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find((alias) =>
      alias?.node?.sources?.includes(Provider.Primary)
    )?.node ??
    getFirstPaginatedNode(insuredEntity?.node.aliases);
  const insuredAddress = getFirstPaginatedNode(insuredEntity?.node.addresses);

  const insuredIndustrySectors = getPaginatedNodes(
    insuredEntity?.node.industrySectors
  );

  const insuredIndustrySector = insuredIndustrySectors.find(
    (industrySector) =>
      industrySector?.sources?.includes(Provider.Primary) ||
      industrySector.primary
  );

  const brokerAlias = getPaginatedNodes(brokerEntity?.node.aliases).find(
    (alias) => alias?.type === AliasType.LegalName && alias?.primary
  );

  const brokerAddress = getPaginatedNodes(brokerEntity?.node?.addresses).find(
    (address) => address?.type?.includes('CORPORATE') && address.primary
  );

  const broker = getPaginatedNodes(submission?.persons).filter((broker) => {
    return broker.personType?.type === 'AGENCY_CONTACT';
  })[0];

  const brokerAgentName = [broker?.firstName, broker?.lastName]
    .filter(Boolean)
    .join(' ');

  const brokerAgentContacts = getPaginatedNodes(broker?.contacts);

  const brokerAgentEmail = brokerAgentContacts.find(
    (contact) =>
      contact.type &&
      contact.type?.toLowerCase() === ContactType.Email.toLowerCase()
  );

  const brokerAgentEmailAddress = brokerAgentEmail?.value;

  const brokerAgentPhone = brokerAgentContacts.find(
    (contact) =>
      contact.type &&
      contact.type?.toLowerCase() === ContactType.Phone.toLowerCase()
  );

  const brokerAgentPhoneNumber = brokerAgentPhone?.value;

  const brokerEntityName = brokerAlias?.name;
  const insuredEntityName = insuredAlias?.name;

  /* Quotes */
  const quotes = getPaginatedNodes(data?.quotes);
  const draftQuotes = quotes.filter(
    (quote) => quote.status === QuoteStatus.Draft
  );
  const quotedQuotes = quotes.filter(
    (quote) => quote.status === QuoteStatus.Quoted
  );
  const boundQuotes = quotes.filter(
    (quote) => quote.status === QuoteStatus.Bound
  );
  const boundQuote = boundQuotes[0];
  const hasBoundQuote = !!boundQuote;
  const shouldHaveBoundQuote =
    submissionStatus &&
    [SubmissionStatus.Bound, SubmissionStatus.PolicyIssued].includes(
      submissionStatus
    );
  const isMissingBoundQuote = !hasBoundQuote && shouldHaveBoundQuote;
  const allowBindingQuote = !submissionStatus
    ? false
    : submission.status !== SubmissionStatus.Quoted
    ? false
    : hasBoundQuote
    ? false
    : ![
        SubmissionStatus.Bound,
        SubmissionStatus.PolicyIssued,
        SubmissionStatus.Cancelled,
        SubmissionStatus.PendingCancellation,
      ].includes(submission.status);

  const allowUnbindQuote = !submission?.status
    ? false
    : hasBoundQuote &&
      ![
        SubmissionStatus.Bound,
        SubmissionStatus.PolicyIssued,
        SubmissionStatus.Cancelled,
        SubmissionStatus.PendingCancellation,
      ].includes(submission.status)
    ? true
    : submission.status === SubmissionStatus.Quoted &&
      ![
        SubmissionStatus.Cancelled,
        SubmissionStatus.PendingCancellation,
      ].includes(submission.status);

  const allowSelectingQuotesForQuotePackage =
    submissionStatus === SubmissionStatus.Evaluation &&
    submissionSubStatus === SubmissionSubStatus.EvaluationNotEditable &&
    quotedQuotes?.length === 0;

  const hasQuotePackageBeenCreated =
    quotedQuotes.length + boundQuotes.length > 0;

  const hasDraftQuotes = draftQuotes.length > 0;

  const allowCreatingQuotes =
    submissionStatus === SubmissionStatus.Evaluation &&
    submissionSubStatus === SubmissionSubStatus.EvaluationNotEditable &&
    !hasQuotePackageBeenCreated;

  const allowSortQuotes =
    submissionStatus === SubmissionStatus.Evaluation &&
    submissionSubStatus === SubmissionSubStatus.EvaluationNotEditable &&
    quotes.length > 1 &&
    !hasQuotePackageBeenCreated;

  const allowCreateQuotePackage =
    hasDraftQuotes &&
    !hasQuotePackageBeenCreated &&
    submissionStatus &&
    ![
      SubmissionStatus.Cancelled,
      SubmissionStatus.PendingCancellation,
    ].includes(submissionStatus);
  const allowUndoQuotePackage =
    submissionStatus === SubmissionStatus.Quoted &&
    !!hasQuotePackageBeenCreated &&
    !hasBoundQuote;

  const submissionUuid = submission?.uuid;

  const submissionStatusShouldBeEvaluationNotEditable =
    submissionStatus === SubmissionStatus.Quoted &&
    quotedQuotes.length + boundQuotes.length === 0;

  const submissionStatusIsWrong = submissionStatusShouldBeEvaluationNotEditable;

  const nextSubmissionStatus = submissionStatusShouldBeEvaluationNotEditable
    ? SubmissionStatus.Evaluation
    : submissionStatus;

  const nextSubmissionSubStatus = submissionStatusShouldBeEvaluationNotEditable
    ? SubmissionSubStatus.EvaluationNotEditable
    : submissionSubStatus;

  const isQuestionaireAndOverviewComplete =
    (submissionStatus === SubmissionStatus.Evaluation &&
      submissionSubStatus === SubmissionSubStatus.EvaluationNotEditable) ||
    (submissionStatus &&
      [
        SubmissionStatus.Bound,
        SubmissionStatus.Lost,
        SubmissionStatus.PolicyIssued,
        SubmissionStatus.Quoted,
      ].includes(submissionStatus));

  const isPolicyIssued = submissionStatus === SubmissionStatus.PolicyIssued;
  const isBound = submissionStatus === SubmissionStatus.Bound;
  const isEvaluation = submissionStatus === SubmissionStatus.Evaluation;
  const isSubmitted = submissionStatus === SubmissionStatus.Submitted;
  const isCleared = submissionStatus === SubmissionStatus.Cleared;
  const documentLink = data?.submission?.documentLink;
  const allowDeclineSubmission =
    submissionStatus &&
    ![
      SubmissionStatus.Declined,
      SubmissionStatus.PolicyIssued,
      SubmissionStatus.Cancelled,
      SubmissionStatus.PendingCancellation,
    ].includes(submissionStatus);

  const allowReferSubmission =
    submissionStatus &&
    ![
      SubmissionStatus.Declined,
      SubmissionStatus.PolicyIssued,
      SubmissionStatus.Referred,
      SubmissionStatus.Cancelled,
      SubmissionStatus.PendingCancellation,
    ].includes(submissionStatus);

  const allowLostSubmission =
    submissionStatus &&
    ![
      SubmissionStatus.Declined,
      SubmissionStatus.PolicyIssued,
      SubmissionStatus.Lost,
      SubmissionStatus.Cancelled,
      SubmissionStatus.PendingCancellation,
    ].includes(submissionStatus);

  let declinationReasons = getSubmissionDeclinationReasonOptions();

  if (
    insuredIndustrySector &&
    isDeclined &&
    submissionSubStatus === SubmissionSubStatus.DeclinedIndustrySector
  ) {
    declinationReasons = declinationReasons.map((option) => {
      if (option.value === SubmissionSubStatus.DeclinedIndustrySector) {
        return {
          ...option,
          because: `the insured is in the ${insuredIndustrySector.name} (${insuredIndustrySector.naicsCode}) industry sector`,
        };
      }
      return option;
    });
  }

  const declinationReason =
    isDeclined && submissionSubStatus
      ? declinationReasons.find(
          (option) => option.value === submissionSubStatus
        )
      : undefined;

  const quotesLength = quotes?.length;
  return {
    quotesLength,
    isPrimarySubmission,
    isExcessSubmission,
    submissionType,
    policyTypeName,
    declinationReasons,
    declinationReason,
    declinedBecause: declinationReason
      ? declinedBecause(declinationReason.because ?? declinationReason.label)
      : undefined,
    isDeclined,
    isReferred,
    shouldHaveBoundQuote,
    isMissingBoundQuote,
    allowDeclineSubmission,
    allowReferSubmission,
    allowLostSubmission,
    submission,
    underwriter,
    application,
    applicationId,
    submissionApplication,
    submissionApplicationUuid,
    insuredLogo,
    insuredEntity,
    insuredAlias,
    insuredEntityDomain,
    insuredEntityDomainName,
    brokerEntity,
    brokerEntityName,
    parentBrokageEntityUuid,
    brokerAlias,
    insuredEntityName,
    allowUnbindQuote,
    allowBindingQuote,
    allowSelectingQuotesForQuotePackage,
    isCleared,
    isSubmitted,
    isEvaluation,
    isBound,
    isPolicyIssued,
    isQuestionaireAndOverviewComplete,
    submissionReferenceId,
    submissionStatus,
    documentLink,
    boundQuote,
    allowCreatingQuotes,
    nextSubmissionStatus,
    submissionStatusShouldBeEvaluationNotEditable,
    submissionStatusIsWrong,
    nextSubmissionSubStatus,
    submissionUuid,
    allowUndoQuotePackage,
    quotedQuotes,
    allowCreateQuotePackage,
    hasQuotePackageBeenCreated,
    brokerAddress,
    brokerAgentEmail,
    brokerAgentEmailAddress,
    brokerAgentContacts,
    brokerAgentPhoneNumber,
    brokerAgentName,
    insuredAddress,
    allowSortQuotes,
    submissionSubStatus,
    propertyAndCasualtyLicenseExpired,
    surplusLinesTaxLicenseExpired,
  };
};

export type SubmissionInfo = ReturnType<typeof getInfo>;

export const getSubmissionDeclinationReasonOptions = () => {
  return [
    {
      value: SubmissionSubStatus.DeclinedIndustrySector,
      label: 'Class of Business',
      because: "of the insured's class of business",
    },
    {
      value: SubmissionSubStatus.DeclinedDuplicate,
      label: 'Duplicate Submission',
      because: 'it is a duplicate submission',
    },
    {
      value: SubmissionSubStatus.DeclinedBor,
      label: 'Submission Received From Another Broker',
      because: 'the submission was received from another broker',
    },
    {
      value: SubmissionSubStatus.DeclinedOfac,
      label: 'OFAC',
      because: 'insured was on OFAC sanctions list',
    },
    {
      value: SubmissionSubStatus.DeclinedLossHistory,
      label: 'Loss History',
      because: "of the insured's loss history",
    },
    {
      value: SubmissionSubStatus.DeclinedBrokerNotAppointed,
      label: 'Broker Not Appointed',
      because: 'the broker was not appointed',
    },
    {
      value: SubmissionSubStatus.DeclinedIncompleteSubmission,
      label: 'Incomplete Submission',
      because: 'the submission was incomplete',
    },
    {
      value: SubmissionSubStatus.DeclinedNoAppetite,
      label: 'No Appetite',
      because: 'there is no business appetite',
    },
    {
      value: SubmissionSubStatus.DeclinedRevenueParams,
      label: 'Revenue Params',
      because: 'of revenue params',
    },
    {
      value: SubmissionSubStatus.DeclinedNoAuthority,
      label: 'No Authority',
      because: 'there is of a lack of authority',
    },
    {
      value: SubmissionSubStatus.DeclinedDuplicate,
      label: 'Duplicate Submission',
      because: 'there is a duplicate submission',
    },
    {
      value: SubmissionSubStatus.DeclinedBor,
      label: 'BOR',
      because: 'Bor',
    },
    {
      value: SubmissionSubStatus.DeclinedNoQuote,
      label: 'No Quote',
      because: 'there is no quote',
    },
    {
      value: SubmissionSubStatus.DeclinedPolicyTermsOrPremium,
      label: 'Policy Terms or Premium',
      because: 'Policy terms or premium',
    },
    {
      value: SubmissionSubStatus.DeclinedCyberExposure,
      label: 'Cyber Exposure',
      because: 'there is cyber exposure',
    },
    {
      value: SubmissionSubStatus.DeclinedErrorsAndOmissionsExposure,
      label: 'Errors and Omissions Exposure',
      because: 'there is errors and omissions exposure',
    },
    {
      value: SubmissionSubStatus.DeclinedLocationJurisdiction,
      label: 'Location Jurisdiction',
      because: 'Location jurisdiction',
    },
    {
      value: SubmissionSubStatus.DeclinedSeverityClaims,
      label: 'Severity Claims',
      because: 'there is severity claims',
    },
    {
      value: SubmissionSubStatus.DeclinedInsufficientControls,
      label: 'Insufficient Controls',
      because: 'there is Insufficient controls',
    },
  ];
};

export const SUBMISSION_DECLINE_REASON_OPTIONS =
  getSubmissionDeclinationReasonOptions();

export const declinedBecause = (because: string) => {
  return `${because}`;
};
